import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Anchor, Edit, MapPin, Trash2 } from 'lucide-react';
import { TransportModesSection } from './TransportModesSection';
import {
  useAddTransportVehiclesToFacility,
  useRemoveTransportVehiclesFromFacility,
} from '~/api/facility';
import type { TransportVehicle } from '~/graphql/graphql';
import { formatPhoneNumber } from '~/components/ui/inputs/phoneNumber';
import { hasText, portDisplay } from '~/utils';
import { formatCityRegionZip } from '~/utils/addressFormat';
import type { Facility } from './OrganizationFacilities';
import { UpsertFacilityModal } from './UpsertFacilityModal';
import { DeleteFacilityDialog } from './DeleteFacilityDialog';
import { ShippingRatesSection } from './ShippingRatesSection';

type Props = {
  facility: Facility;
  allVehicles: TransportVehicle[];
  canDelete: boolean;
  onDeleted: (id: string) => void;
};

export function FacilityCard({
  facility,
  allVehicles,
  canDelete,
  onDeleted,
}: Props) {
  const { t } = useTranslation('settings');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>(() =>
    facility.transportVehicles.map((vehicle) => vehicle.id)
  );
  const [pendingVehicleIds, setPendingVehicleIds] = useState<Set<string>>(
    () => new Set()
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedVehicleIds(
      facility.transportVehicles.map((vehicle) => vehicle.id)
    );
  }, [facility.transportVehicles]);

  const addVehicle = useAddTransportVehiclesToFacility();
  const removeVehicle = useRemoveTransportVehiclesFromFacility();

  function handleToggle(vehicleId: string, isCurrentlySelected: boolean) {
    if (pendingVehicleIds.has(vehicleId)) return;

    setSelectedVehicleIds((prev) =>
      isCurrentlySelected
        ? prev.filter((id) => id !== vehicleId)
        : [...prev, vehicleId]
    );
    setPendingVehicleIds((prev) => new Set(prev).add(vehicleId));

    const variables = {
      facilityId: facility.id,
      transportVehicleIds: [vehicleId],
    };

    const onSuccess = () => {
      setPendingVehicleIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleId);
        return next;
      });
      toast.success(t('settings.facilities.vehicle_toggle_success'));
    };

    const onError = () => {
      setSelectedVehicleIds((prev) =>
        isCurrentlySelected
          ? [...prev, vehicleId]
          : prev.filter((id) => id !== vehicleId)
      );
      setPendingVehicleIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleId);
        return next;
      });
      toast.error(t('settings.facilities.vehicle_toggle_error'));
    };

    if (isCurrentlySelected) {
      removeVehicle.mutate(variables, { onSuccess, onError });
    } else {
      addVehicle.mutate(variables, { onSuccess, onError });
    }
  }

  const cityStateZip = formatCityRegionZip(facility.address);
  const hasLocations = facility.locations.length > 0;

  const locationChips = facility.locations
    .filter((location) => hasText(location.code))
    .map((location) => ({
      id: location.id,
      label: hasText(location.branchCode)
        ? `${location.branchCode.trim()} · ${location.code.trim()}`
        : location.code.trim(),
    }));

  return (
    <>
      <div
        className="border border-neut-200 rounded-lg p-4 hover:border-prim transition-colors"
        data-testid="facility-card"
      >
        <div className="flex items-center justify-between">
          <div className="mb-1">
            <h3 className="text-sm font-semibold text-neut-prim mb-1">
              {facility.address.addressLine1}
            </h3>
            {facility.address.addressLine2 && (
              <p className="text-sm text-neut-sec">
                {facility.address.addressLine2}
              </p>
            )}
            <p className="text-sm text-neut-sec">{cityStateZip}</p>
            {hasText(facility.phoneNumber) && (
              <p className="text-sm text-neut-sec" data-testid="facility-phone">
                {t('settings.facilities.phone_display', {
                  phone: formatPhoneNumber(facility.phoneNumber),
                })}
              </p>
            )}
            {hasText(facility.faxNumber) && (
              <p className="text-sm text-neut-sec" data-testid="facility-fax">
                {t('settings.facilities.fax_display', {
                  fax: formatPhoneNumber(facility.faxNumber),
                })}
              </p>
            )}
            <p
              className="text-xs text-neut-400 mt-1 flex items-center gap-1"
              data-testid="facility-location-count"
            >
              <MapPin size={12} />
              {t('settings.facilities.used_by_locations', {
                count: facility.locations.length,
              })}
            </p>
            {locationChips.length > 0 && (
              <div
                className="flex flex-wrap gap-1 mt-1"
                data-testid="facility-location-codes"
              >
                {locationChips.map((chip) => (
                  <span
                    key={chip.id}
                    className="text-xs text-neut-700 bg-neut-100 rounded px-1.5 py-0.5"
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            )}
            {facility.nearestPort && (
              <p className="text-sm text-neut-sec flex items-center gap-1">
                <Anchor size={12} className="text-prim" />
                {t('settings.facilities.nearest_port_label')}{' '}
                {t('settings.facilities.port_name', {
                  name: portDisplay(facility.nearestPort).label,
                })}
              </p>
            )}
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              data-testid="edit-facility-button"
              className="text-neut cursor-pointer"
              aria-label={t('settings.facilities.edit_aria')}
              onClick={() => setShowEditModal(true)}
            >
              <Edit className="text-neut cursor-pointer" size={17} />
            </button>

            <span
              title={
                hasLocations
                  ? t('settings.facilities.delete_blocked_has_locations')
                  : undefined
              }
            >
              <button
                type="button"
                data-testid="delete-facility-button"
                className="text-error-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                aria-label={t('settings.facilities.delete_aria')}
                disabled={!canDelete || hasLocations}
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 size={17} />
              </button>
            </span>
          </div>
        </div>

        <TransportModesSection
          allVehicles={allVehicles}
          selectedVehicleIds={selectedVehicleIds}
          pendingVehicleIds={pendingVehicleIds}
          onToggle={handleToggle}
        />
        <ShippingRatesSection
          facilityId={facility.id}
          initialRates={facility.shippingRates}
        />
      </div>

      {showEditModal && (
        <UpsertFacilityModal
          isOpen={true}
          onClose={() => setShowEditModal(false)}
          allVehicles={allVehicles}
          facility={facility}
        />
      )}

      <DeleteFacilityDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        facility={facility}
        onDeleted={onDeleted}
      />
    </>
  );
}
